import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Bell, CheckCheck, Menu, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Topbar({ title, onMenuClick }) {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen]     = useState(false);
  const panelRef = useRef(null);
  const { logout } = useAuth();
  const navigate   = useNavigate();

  const unread = notifs.filter(n => !n.is_read).length;

  useEffect(() => {
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadNotifs = async () => {
    try { const res = await notificationAPI.getAll(); setNotifs(res.data); } catch {}
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
  };

  const handleMarkAll = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  return (
    <header className="topbar">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="hamburger-btn" onClick={onMenuClick} aria-label="Abrir menú">
          <Menu size={22} />
        </button>
        <div className="topbar-title">{title}</div>
      </div>

      <div className="topbar-actions">
        {/* Notificaciones */}
        <div style={{ position: 'relative' }} ref={panelRef}>
          <button className="notif-btn" onClick={() => setOpen(o => !o)} aria-label="Notificaciones">
            <Bell size={18} strokeWidth={1.8} />
            {unread > 0 && (
              <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>
            )}
          </button>

          {open && (
            <div className="notif-panel">
              <div style={{
                padding: '12px 14px', borderBottom: '1px solid var(--gray-100)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Notificaciones</span>
                {unread > 0 && (
                  <button onClick={handleMarkAll} style={{
                    fontSize: 12, color: 'var(--primary)', background: 'none',
                    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <CheckCheck size={13} /> Leer todas
                  </button>
                )}
              </div>

              {notifs.length === 0 ? (
                <div style={{ padding: '28px 14px', textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
                  Sin notificaciones
                </div>
              ) : (
                notifs.slice(0, 20).map(n => (
                  <div
                    key={n.id}
                    className={`notif-item ${!n.is_read ? 'unread' : ''}`}
                    onClick={() => handleMarkRead(n.id)}
                  >
                    <div className="notif-item-title">{n.title}</div>
                    <div className="notif-item-msg">{n.message}</div>
                    <div className="notif-item-time">
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: es })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        {/* Cerrar sesión — visible en topbar siempre */}
        <button
          className="notif-btn"
          onClick={handleLogout}
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
        >
          <LogOut size={17} strokeWidth={1.8} />
        </button>
      </div>
    </header>
  );
}
